import React, { useCallback } from 'react'

import { formatDateRangeLabel } from 'components/date-range-picker/date-defaults'
import { DateRangeSelector } from 'components/date-range-picker/DateRangeSelector/DateRangeSelector'
import type { Location } from 'trackers/shared-events'
import { trackDateRangeChanged } from 'trackers/shared-events'
import type { Surface } from 'types/project'

// A ChartPanel's date range, in Unix seconds — declared locally rather than
// imported from features/charts-v2's ChartPanel type, since this component
// lives in the shared layer and must not import feature code (see
// `shared-imports-features`). Keywords, Feedback, and Charts v2 all persist
// the same { startDate, endDate } shape.
export interface PanelDateRange {
  startDate: number
  endDate: number
}

type PanelDateRangeControlProps = {
  dateRange: PanelDateRange
  onChange: (dateRange: PanelDateRange) => void
  surface: Surface
  location?: Location
}

/**
 * Wires the Sutra-styled `DateRangeSelector` to a ChartPanel's date range —
 * the seconds-based storage shape shared by Keywords', Feedback's, and
 * Charts v2's panel stores. Converts to/from `Date` for `DateRangeSelector`
 * and reports `trackDateRangeChanged`, so call sites don't hand-roll the
 * seconds↔ms conversion (a detail that's easy to get right once and drift on
 * a second copy-paste).
 */
export const PanelDateRangeControl: React.FC<PanelDateRangeControlProps> = ({
  dateRange,
  onChange,
  surface,
  location = 'header',
}) => {
  const handleDateRangeChange = useCallback(
    (
      startDate: Date,
      endDate: Date,
      source?: 'shorthand' | 'xtd' | 'custom',
      presetKey?: string | null,
    ) => {
      const spanDays = Math.max(
        0,
        Math.round((endDate.getTime() - startDate.getTime()) / 86400000),
      )
      trackDateRangeChanged({
        surface,
        location,
        range_type: source && source !== 'custom' ? 'preset' : 'custom',
        preset:
          source && source !== 'custom'
            ? formatDateRangeLabel(startDate, endDate, source, presetKey)
            : undefined,
        span_days: spanDays,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      })
      onChange({
        startDate: Math.floor(startDate.getTime() / 1000),
        endDate: Math.floor(endDate.getTime() / 1000),
      })
    },
    [surface, location, onChange],
  )

  return (
    <DateRangeSelector
      dateRange={{
        startDate: new Date(dateRange.startDate * 1000),
        endDate: new Date(dateRange.endDate * 1000),
      }}
      onDateRangeChange={handleDateRangeChange}
    />
  )
}
